import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Plus, Clock, TrendingUp, Package, AlertCircle, Check, ChevronDown, ChevronUp, ArrowUpDown } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { PickListCard } from '../components/picking/PickListCard';
import { useAuthStore } from '../stores/authStore';
import { usePickingStore } from '../stores/pickingStore';
import { useAppStore } from '../stores/appStore';
import type { PickList } from '../types';

export const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { loadUserPickLists, setCurrentPickList, isLoading } = usePickingStore();
  const { addNotification } = useAppStore();
  const [pickLists, setPickLists] = useState<PickList[]>([]);
  const [activeTab, setActiveTab] = useState<'active' | 'completed'>('active');
  const [statsExpanded, setStatsExpanded] = useState(true);
  const [sortBy, setSortBy] = useState<'priority' | 'overdue'>('priority');
  const [stats, setStats] = useState({
    totalPicks: 0,
    completedToday: 0,
    accuracy: 0,
    efficiency: 0
  });

  useEffect(() => {
    loadData();
    
    // Set up periodic refresh to check for new data
    const interval = setInterval(() => {
      console.log('📊 Dashboard: Periodic data refresh...');
      loadData();
    }, 30000); // Refresh every 30 seconds
    
    return () => clearInterval(interval);
  }, [user]);

  const loadData = async () => {
    if (!user) return;

    console.log('📊 Dashboard: Loading data for user:', {
      id: user.id,
      email: user.email,
      role: user.role
    });

    try {
      const lists = await loadUserPickLists(user.id);
      console.log('📊 Dashboard: Loaded pick lists:', {
        count: lists.length,
        lists: lists.map(l => ({
          id: l.id,
          orderNumber: l.orderNumber,
          status: l.status,
          customerName: l.customerName,
          totalItems: l.totalItems,
          pickedItems: l.pickedItems
        }))
      });
      setPickLists(lists);
    
      // Calculate stats from actual data
      const completed = lists.filter(list => list.status === 'completed');
      const inProgress = lists.filter(list => list.status === 'in_progress');
      const assigned = lists.filter(list => list.status === 'assigned' || list.status === 'pending');
      const totalItems = lists.reduce((sum, list) => sum + list.totalItems, 0);
      const pickedItems = lists.reduce((sum, list) => sum + list.pickedItems, 0);
      const accuracy = totalItems > 0 ? Math.round((pickedItems / totalItems) * 100) : 0;
    
      console.log('📊 Dashboard: Calculated stats:', {
        totalPicks: lists.length,
        completedToday: completed.length,
        inProgress: inProgress.length,
        assigned: assigned.length,
        accuracy,
        totalItems,
        pickedItems,
        detailedLists: lists.map(l => ({ 
          orderNumber: l.orderNumber, 
          status: l.status, 
          totalItems: l.totalItems, 
          pickedItems: l.pickedItems 
        }))
      });

      setStats({
        totalPicks: lists.length,
        completedToday: completed.length,
        accuracy: Math.min(accuracy, 100), // Cap at 100%
        efficiency: lists.length > 0 ? Math.round((completed.length / lists.length) * 100) : 0
      });
    } catch (error) {
      console.error('📊 Dashboard: Failed to load pick lists:', error);
      addNotification({
        type: 'error',
        title: 'Loading Error',
        message: 'Failed to load pick lists from database',
      });
    }
  };

  const sortPickLists = (lists: PickList[], sortType: 'priority' | 'overdue'): PickList[] => {
    const sortedLists = [...lists];
    
    if (sortType === 'priority') {
      // Sort by priority: urgent > high > medium > low
      const priorityOrder = { 'urgent': 0, 'high': 1, 'medium': 2, 'low': 3 };
      return sortedLists.sort((a, b) => {
        const aPriority = priorityOrder[a.priority as keyof typeof priorityOrder] ?? 4;
        const bPriority = priorityOrder[b.priority as keyof typeof priorityOrder] ?? 4;
        return aPriority - bPriority;
      });
    } else {
      // Sort by overdue status: overdue items first, then by due date
      return sortedLists.sort((a, b) => {
        const now = new Date();
        const aOverdue = a.dueDate && new Date(a.dueDate) < now;
        const bOverdue = b.dueDate && new Date(b.dueDate) < now;
        
        // Overdue items first
        if (aOverdue && !bOverdue) return -1;
        if (!aOverdue && bOverdue) return 1;
        
        // If both overdue or both not overdue, sort by due date
        if (a.dueDate && b.dueDate) {
          return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
        }
        
        // Items with due dates come before items without
        if (a.dueDate && !b.dueDate) return -1;
        if (!a.dueDate && b.dueDate) return 1;
        
        return 0;
      });
    }
  };

  const handlePickListClick = (pickList: PickList) => {
    setCurrentPickList(pickList);
    navigate(`/picking/${pickList.id}`);
  };

  const activeLists = pickLists.filter(list => 
    list.status === 'assigned' || list.status === 'in_progress'
  );
  
  const completedLists = pickLists.filter(list => 
    list.status === 'completed'
  );

  // Apply sorting to the lists
  const sortedActiveLists = sortPickLists(activeLists, sortBy);
  const sortedCompletedLists = sortPickLists(completedLists, sortBy);
  if (isLoading) {
    return (
      <LoadingSpinner />
    );
  }

  return (
    <div className="p-4 sm:p-6 pb-20 sm:pb-24 max-w-4xl mx-auto min-h-screen">
      {/* Welcome Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-2"
      >
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
              Welcome back, {user?.firstName || 'Picker'}!
            </h1>
            <div className="flex items-center justify-between">
              <p className="text-sm sm:text-base text-gray-600">
                Here's your picking overview for today
              </p>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setStatsExpanded(!statsExpanded)}
                className="p-1 text-green-600 hover:text-green-700 hover:bg-green-50 rounded-lg transition-colors touch-manipulation ml-4"
                title={statsExpanded ? 'Collapse overview' : 'Expand overview'}
              >
                {statsExpanded ? (
                  <ChevronUp className="w-7 h-7" />
                ) : (
                  <ChevronDown className="w-7 h-7" />
                )}
              </motion.button>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Stats Overview */}
      {statsExpanded && (
        <motion.div
          initial={{ opacity: 0, y: -20, height: 0 }}
          animate={{ opacity: 1, y: 0, height: 'auto' }}
          exit={{ opacity: 0, y: -20, height: 0 }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
          className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6 overflow-hidden mt-2 pb-4 sm:pb-6 border-b border-saavi-500"
        >
          <Card padding="sm">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-saavi-100 rounded-lg flex items-center justify-center">
                <Package className="w-4 h-4 sm:w-5 sm:h-5 text-saavi-600" />
              </div>
              <div>
                <p className="text-lg sm:text-2xl font-bold text-gray-900">{stats.totalPicks}</p>
                <p className="text-xs sm:text-sm text-gray-600">Total Picks</p>
              </div>
            </div>
          </Card>

          <Card padding="sm">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-saavi-100 rounded-lg flex items-center justify-center">
                <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-saavi-600" />
              </div>
              <div>
                <p className="text-lg sm:text-2xl font-bold text-gray-900">{stats.completedToday}</p>
                <p className="text-xs sm:text-sm text-gray-600">Completed</p>
              </div>
            </div>
          </Card>

          <Card padding="sm">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-saavi-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-saavi-600" />
              </div>
              <div>
                <p className="text-lg sm:text-2xl font-bold text-gray-900">{stats.accuracy}%</p>
                <p className="text-xs sm:text-sm text-gray-600">Accuracy</p>
              </div>
            </div>
          </Card>

          <Card padding="sm">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-saavi-100 rounded-lg flex items-center justify-center">
                <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-saavi-600" />
              </div>
              <div>
                <p className="text-lg sm:text-2xl font-bold text-gray-900">{stats.efficiency}%</p>
                <p className="text-xs sm:text-sm text-gray-600">Efficiency</p>
              </div>
            </div>
          </Card>
        </motion.div>
      )}

      {/* Pick Lists with Tabs */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="mb-4 sm:mb-6"
      >
        {/* Tab Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-1 bg-gray-100 rounded-lg p-1 w-full max-w-md">
            <button
              onClick={() => setActiveTab('active')}
              className={`
                flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-md text-sm font-medium transition-all
                ${activeTab === 'active'
                  ? 'bg-white text-green-800 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
                }
              `}
            >
              <Package className={`w-4 h-4 ${activeTab === 'active' ? 'text-green-600' : ''}`} />
              <span className="text-base">Active</span>
              {activeLists.length > 0 && (
                <span className={`
                  px-2 py-1 rounded-full text-xs font-medium
                  ${activeTab === 'active' 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-gray-200 text-gray-600'
                  }
                `}>
                  {activeLists.length}
                </span>
              )}
            </button>
            
            <button
              onClick={() => setActiveTab('completed')}
              className={`
                flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-md text-sm font-medium transition-all
                ${activeTab === 'completed'
                  ? 'bg-white text-saavi-800 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
                }
              `}
            >
              <Check className="w-4 h-4" />
              <span className="text-base">Completed</span>
              {completedLists.length > 0 && (
                <span className={`
                  px-2 py-1 rounded-full text-xs font-medium
                  ${activeTab === 'completed' 
                    ? 'bg-saavi-100 text-saavi-800' 
                    : 'bg-gray-200 text-gray-600'
                  }
                `}>
                  {completedLists.length}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Sort Dropdown */}
        <div className="flex justify-end mb-4">
          <div className="relative">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'priority' | 'overdue')}
              className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-8 text-sm font-medium text-gray-700 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-saavi-500 focus:border-saavi-500"
            >
              <option value="priority">Sort by Priority</option>
              <option value="overdue">Sort by Overdue</option>
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
              <ArrowUpDown className="w-4 h-4 text-gray-400" />
            </div>
          </div>
        </div>

        {/* Tab Content */}
        <div className="space-y-3 sm:space-y-4">
          {activeTab === 'active' ? (
            sortedActiveLists.length > 0 ? (
              sortedActiveLists.map((pickList) => (
                <PickListCard
                  key={pickList.id}
                  pickList={pickList}
                  onClick={handlePickListClick}
                />
              ))
            ) : (
              <Card className="text-center py-6 sm:py-8">
                <Package className="w-10 h-10 sm:w-12 sm:h-12 text-saavi-600 mx-auto mb-4" />
                <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">
                  {pickLists.length === 0 ? 'No pick lists found' : 'No active pick lists'}
                </h3>
                <p className="text-sm sm:text-base text-gray-600 mb-4">
                  {pickLists.length === 0 
                    ? 'Check your database connection and ensure pick lists are assigned to your user.' 
                    : 'You\'re all caught up! New orders will appear here.'
                  }
                </p>
                <Button
                  onClick={() => navigate('/scanner')}
                  icon={<Plus className="w-4 h-4" />}
                >
                  Start Scanning
                </Button>
              </Card>
            )
          ) : (
            sortedCompletedLists.length > 0 ? (
              sortedCompletedLists.map((pickList) => (
                <PickListCard
                  key={pickList.id}
                  pickList={pickList}
                  onClick={handlePickListClick}
                />
              ))
            ) : (
              <Card className="text-center py-6 sm:py-8">
                <Check className="w-10 h-10 sm:w-12 sm:h-12 text-saavi-600 mx-auto mb-4" />
                <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">
                  No completed pick lists
                </h3>
                <p className="text-sm sm:text-base text-gray-600">
                  Completed pick lists will appear here once you finish picking orders.
                </p>
              </Card>
            )
          )}
        </div>
      </motion.div>

      {/* Add state for active tab */}
    </div>
  );
};